import {
  Box,
  Button,
  VStack,
  Input,
  useToast,
  Text,
  Checkbox,
  useColorModeValue,
  Heading,
  HStack,
} from "@chakra-ui/react";
import { useState, useEffect, useRef } from "react";
import axios from "axios";

interface PasswordChangeRequest {
  old_password: string;
  new_password: string;
  confirm_new_password: string;
}

interface ChangePasswordProps {
  handleLogout: () => void;
}

const ChangeB2CPassword = ({ handleLogout }: ChangePasswordProps) => {
  const [passwordChangeData, setPasswordChangeData] =
    useState<PasswordChangeRequest>({
      old_password: "",
      new_password: "",
      confirm_new_password: "",
    });

  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isInitialPassword, setIsInitialPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toast = useToast();

  const bannerColor = useColorModeValue("yellow.100", "yellow.700");

  /*
   * Prevent authentication checks / effects from running again
   * after a successful password change.
   */
  const passwordChangedRef = useRef(false);

  /*
   * Read the current B2C token once when the component mounts.
   *
   * IMPORTANT:
   * Absence of a token is NOT treated as a toast-worthy error here.
   * The router/authentication layer is responsible for authentication
   * and redirection.
   */
  useEffect(() => {
    if (passwordChangedRef.current) {
      return;
    }

    const token = localStorage.getItem("b2c_token");

    if (!token) {
      return;
    }

    try {
      const payload = token.split(".")[1];

      if (!payload) {
        return;
      }

      const decodedToken = JSON.parse(atob(payload));

      setIsInitialPassword(
        decodedToken?.is_initial_password === true
      );
    } catch (error) {
      console.error(
        "Unable to decode B2C authentication token:",
        error
      );
    }
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;

    setPasswordChangeData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (name === "new_password") {
      validatePassword(value);
    }
  };

  const validatePassword = (password: string) => {
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;

    if (!passwordRegex.test(password)) {
      setPasswordError(
        "Password must be at least 8 characters long, contain one uppercase letter, one lowercase letter, one number, and one special character (@ $ ! % * ? & #)."
      );
    } else {
      setPasswordError(null);
    }
  };

  const handleChangePassword = async () => {
    if (isSubmitting || passwordChangedRef.current) {
      return;
    }

    const {
      old_password,
      new_password,
      confirm_new_password,
    } = passwordChangeData;

    if (!old_password || !new_password || !confirm_new_password) {
      toast({
        title: "Please complete all password fields.",
        status: "error",
      });
      return;
    }

    if (new_password !== confirm_new_password) {
      toast({
        title: "Passwords do not match!",
        status: "error",
      });
      return;
    }

    if (passwordError) {
      toast({
        title: "Please resolve password errors before submitting.",
        status: "error",
      });
      return;
    }

    const token = localStorage.getItem("b2c_token");

    /*
     * Do not show "User not authenticated" here.
     *
     * If authentication has disappeared, the authentication/router
     * layer is responsible for handling it.
     */
    if (!token) {
      return;
    }

    setIsSubmitting(true);

    const payload = {
      old_password,
      new_password,
    };

    try {
      const apiurl =
        process.env.REACT_APP_API_URL ||
        "https://default-url.com";

      await axios.put(
        `${apiurl}/b2c/student/change-password`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      /*
       * Mark the operation as completed BEFORE triggering logout.
       *
       * This prevents the authentication change caused by logout
       * from being interpreted as an authentication error by this
       * component.
       */
      passwordChangedRef.current = true;

      toast({
        title: "Password Updated Successfully",
        status: "success",
      });

      setPasswordChangeData({
        old_password: "",
        new_password: "",
        confirm_new_password: "",
      });

      /*
       * ONE logout mechanism.
       *
       * handleLogout() is responsible for:
       *   - removing authentication state
       *   - removing the B2C token
       *   - resetting authentication state
       *   - navigating to the appropriate login page
       *
       * Do not remove the token or navigate again here.
       */
      await Promise.resolve(handleLogout());
    } catch (error) {
      console.error("Error updating password:", error);

      setIsSubmitting(false);

      if (axios.isAxiosError(error)) {
        const detail = error.response?.data?.detail;

        toast({
          title: "Password Change Request Failed",
          description:
            typeof detail === "string"
              ? detail
              : "Unable to update your password. Please try again.",
          status: "error",
        });
      } else {
        toast({
          title: "Password Change Request Failed",
          description:
            "Unable to update your password. Please try again.",
          status: "error",
        });
      }
    }
  };

  return (
    <Box>
      <Box
        bgColor={bannerColor}
        p={4}
        height="70px"
      >
        <HStack>
          <Heading size="sm">
            Hello!
          </Heading>

          <Text
            fontWeight="normal"
            fontSize="md"
          >
            {isInitialPassword
              ? "Please change your initial Password and login again to continue"
              : "Change your password"}
          </Text>
        </HStack>
      </Box>

      <Box
        maxW="350px"
        mx="auto"
        my={8}
        border="1px solid"
        borderColor="gray.400"
        borderRadius="md"
        mt={20}
        p={4}
        mb={6}
      >
        <VStack spacing={4}>
          <Input
            placeholder="Current Password"
            name="old_password"
            type={showPassword ? "text" : "password"}
            value={passwordChangeData.old_password}
            onChange={handleChange}
            isDisabled={isSubmitting}
            required
          />

          <Input
            placeholder="New Password"
            name="new_password"
            type={showPassword ? "text" : "password"}
            value={passwordChangeData.new_password}
            onChange={handleChange}
            isDisabled={isSubmitting}
            required
          />

          {passwordError && (
            <Text color="red.500" fontSize="sm">
              {passwordError}
            </Text>
          )}

          <Input
            placeholder="Confirm New Password"
            name="confirm_new_password"
            type={showPassword ? "text" : "password"}
            value={passwordChangeData.confirm_new_password}
            onChange={handleChange}
            isDisabled={isSubmitting}
            required
          />

          <Checkbox
            isChecked={showPassword}
            onChange={(e) =>
              setShowPassword(e.target.checked)
            }
            isDisabled={isSubmitting}
          >
            Show Password
          </Checkbox>
        </VStack>

        <VStack mt={5} spacing={4}>
          <Button
            border="2px solid"
            borderColor="blue.200"
            borderRadius="md"
            width={40}
            onClick={handleChangePassword}
            isDisabled={
              isSubmitting || !!passwordError
            }
            isLoading={isSubmitting}
            loadingText="Updating..."
          >
            Change Password
          </Button>
        </VStack>
      </Box>
    </Box>
  );
};

export default ChangeB2CPassword;

